// src/utils/transformListing.js
// ─────────────────────────────────────────────────────────────────────────────
import { imgUrl } from "./imgUrl.js";
import { isPubliclyRoutable } from './publicRoutability.js';
// ─────────────────────────────────────────────────────────────────────────────
// Platform-standard listing transformer.
// Takes a raw DB row (Supabase) or static data entry and returns a normalised
// card-ready object. All pages must use this, no page-specific transform logic.
//
// Output shape:
// {
//   id, name, slug,
//   region, regionSlug, countrySlug, city, citySlug,
//   imgs[],
//   desc,
//   priceFrom,      ← formatted display string e.g. "£12,000"
//   priceFromRaw,   ← raw number for filtering/sorting, null if unknown
//   capacity,       ← number or null
//   rating,         ← number or null
//   reviews,        ← number or null
//   verified,       ← boolean
//   featured,       ← boolean
//   lwdScore,       ← number or null
//   tag,            ← string badge or null
//   styles[],       ← array of style strings
//   includes[],     ← array of inclusion/amenity strings
//   showcaseUrl,    ← string or null
//   lat, lng,       ← numbers or null
//   online,         ← boolean
//   type,           ← "venue" | "vendor" | "planner"
//   category,       ← category slug e.g. "wedding-venues" | "photographers"
//   createdAt,      ← timestamp or null
// }
// ─────────────────────────────────────────────────────────────────────────────

// ── Category slug → human label ──────────────────────────────────────────────
const CATEGORY_LABELS = {
  'wedding-venues':   'Wedding Venue',
  'wedding-planners': 'Wedding Planner',
  'photographers':    'Photographer',
  'videographers':    'Videographer',
  'florists':         'Florist',
  'caterers':         'Caterer',
  'entertainment':    'Entertainment',
  'hair-makeup':      'Hair & Makeup',
  'celebrants':       'Celebrant',
  'styling-decor':    'Styling & Decor',
  'event-production': 'Event Production',
  'luxury-transport': 'Luxury Transport',
};

export function categoryLabel(slug) {
  if (!slug) return null;
  return CATEGORY_LABELS[slug] || slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// ── Slug generator, used as fallback when DB row has no slug ────────────────
function makeSlug(str) {
  return (str || "")
    .toLowerCase()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9\s-]/g, " ")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

// ── Currency formatter ────────────────────────────────────────────────────────
function formatPrice(raw, currency = "GBP") {
  if (!raw) return "";
  if (typeof raw === "string" && (raw.includes("£") || raw.includes("€") || raw.includes("$"))) return raw;
  const num = parseInt(raw, 10);
  if (isNaN(num)) return String(raw);
  const sym = currency === "EUR" ? "€" : currency === "USD" ? "$" : "£";
  return `${sym}${num.toLocaleString("en-GB")}`;
}

// ── Single image src extractor ────────────────────────────────────────────────
// Accepts a string URL or a rich img object {src, url, ...} and returns a string.
// Use this everywhere you need a plain URL from v.imgs[n].
/**
 * imgSrc, extract an image URL from a string or rich image object, and route it
 * through the Supabase render/image transform (resize + WebP).
 *
 * Previously this returned the RAW /object/public/ URL, so every caller shipped
 * the full-size original: listing pages alone were serving 18 untransformed
 * images each. Transforming here means the transform "can't be forgotten".
 *
 * resize:'contain' scales proportionally without cropping, so the delivered frame
 * is visually identical to the original, just smaller and WebP. Callers that
 * need a different size pass { width } (see LuxuryVendorCard, VenueProfile).
 */
export function imgSrc(item, opts) {
  if (!item) return "";
  const raw = typeof item === "string" ? item : (item.src || item.url || "");
  if (!raw) return "";
  return imgUrl(raw, { width: 1200, quality: 75, resize: "contain", ...(opts || {}) });
}

/** rawImgSrc, the untransformed URL. Only for identity/tracking, never for <img src>. */
export function rawImgSrc(item) {
  if (!item) return "";
  return typeof item === "string" ? item : (item.src || item.url || "");
}

// ── Image array normaliser ────────────────────────────────────────────────────
function normaliseImgs(l) {
  if (Array.isArray(l.imgs) && l.imgs.length > 0) {
    return l.imgs
      .map((img) => (typeof img === "string" ? img : img?.src || img?.url || ""))
      .filter(Boolean);
  }
  if (l.heroImage) return [l.heroImage];
  if (l.image)     return [l.image];
  if (l.img)       return [l.img];
  return [];
}

// ── Main transformer ──────────────────────────────────────────────────────────
export function transformListing(l, overrides = {}) {
  const currency = l.priceCurrency || "GBP";
  const priceRaw = l.priceFrom != null ? parseInt(l.priceFrom, 10) : null;

  return {
    // Identity
    id:          l.id          ?? null,
    name:        l.cardTitle   || l.name        || "",
    // Use DB slug if present; otherwise auto-generate from name so vendor cards
    // always have a navigable URL (e.g. "Fiore Events" → "fiore-events")
    slug:        l.slug        || makeSlug(l.cardTitle || l.name || ""),

    // Location
    country:     l.country     || "",
    region:      l.region      || "",
    regionSlug:  l.regionSlug  || l.region_slug || "",
    countrySlug: l.countrySlug || l.country_slug || "",
    // snake_case aliases, LuxuryVendorCard reads these for URL building
    country_slug:  l.countrySlug  || l.country_slug  || "",
    region_slug:   l.regionSlug   || l.region_slug   || "",
    category_slug: l.categorySlug || l.category_slug || "",
    city:        l.city        || "",
    citySlug:    l.citySlug    || l.city_slug   || "",

    // Media, preserve rich img objects (credits etc); normaliseImgs as fallback
    imgs:        (Array.isArray(l.imgs) && l.imgs.length > 0) ? l.imgs : normaliseImgs(l),
    videoUrl:    l.videoUrl    || null,

    // Description
    desc:        l.cardSummary || l.shortDescription || l.desc || "",

    // Pricing
    priceFrom:   formatPrice(l.priceFrom, currency),
    priceFromRaw: isNaN(priceRaw) ? null : priceRaw,

    // Specs
    capacity:    l.capacityMax  ?? l.capacityMin ?? l.capacity ?? null,

    // Editorial
    rating:      l.rating       ?? null,
    reviews:     l.reviewCount  ?? l.reviews     ?? null,
    verified:    l.isVerified   ?? l.verified    ?? false,
    featured:    l.isFeatured   ?? l.featured    ?? false,
    lwdScore:    l.lwdScore     ?? null,
    tag:         l.cardBadge    || l.tag          || null,

    // Taxonomy
    styles:      Array.isArray(l.styles)      ? l.styles      : [],
    specialties: Array.isArray(l.specialties) ? l.specialties : [],
    includes:    Array.isArray(l.amenities)   ? l.amenities   :
                 Array.isArray(l.includes)    ? l.includes    : [],

    // Filter fields, must match filterConfig dataField values
    priceLabel:  l.priceLabel  || l.price_label  || "",
    serviceTier: l.serviceTier || l.service_tier || "",

    // Showcase
    /* showcaseUrl gated 2026-08-15: `showcaseEnabled` is the LISTING's flag and says nothing about whether the showcase row is published. Villa Nascosta had the flag on and a DRAFT showcase, so this expression emitted /showcase/villa-nascosta from the homepage, /italy and every card that rendered it, and every one 404'd. This exact expression existed in FIVE files; all five now ask the one gate, which consults the showcase's own state. */ 
    showcaseUrl: l.slug && isPubliclyRoutable(`/showcase/${l.slug}`) ? `/showcase/${l.slug}` : null,

    // Geo
    lat:         l.lat          ?? null,
    lng:         l.lng          ?? null,

    // Meta
    online:        l.online       ?? true,
    // Normalise to "venue" | "vendor", listingType from DB can be raw category slugs
    // (e.g. "bridal-boutiques"), so anything non-venue maps to "vendor"
    type: (() => {
      const raw = l.listingType || l.type || "";
      if (!raw || raw === "venue" || raw === "wedding-venues" || raw === "venues") return "venue";
      return "vendor";
    })(),
    category:      l.category     || l.categorySlug || l.category_slug || "wedding-venues",
    // categorySlug is the canonical kebab-case key used by cards, chips, and
    // feature checks (e.g. Virtual Try-On for 'bridal-boutiques').
    categorySlug:  l.categorySlug || l.category_slug || l.category || "wedding-venues",
    categoryLabel: categoryLabel(l.category || l.categorySlug || l.category_slug || null),
    createdAt:   l.createdAt    ?? l.created_at ?? null,

    // Quality tier & editorial approval
    contentScore:         l.contentQualityScore ?? null,
    editorialApproved:    l.editorialApproved   ?? false,
    editorialFactChecked: l.editorialFactChecked ?? false,

    // Bridal boutique feature flags
    bridalVirtualTryon: l.bridalVirtualTryon ?? l.bridal_virtual_tryon ?? false,

    // Allow page-specific fields to be merged in without custom transforms
    ...overrides,
  };
}

// ── Batch transform ───────────────────────────────────────────────────────────
export function transformListings(rows = [], overrides = {}) {
  return rows.map((l) => transformListing(l, overrides));
}

// ── Dev-only canonical shape assertion ────────────────────────────────────────
// Call at the data entry point of any card-rendering surface.
// Logs to console.warn in development; no-op in production.
// Does NOT throw; just surfaces the problem visibly during development.
export function assertCanonical(item, surface = "unknown") {
  if (!import.meta.env.DEV) return;
  const required = ["id", "name", "slug", "type", "categorySlug"];
  const missing = required.filter(
    (k) => item[k] == null || item[k] === ""
  );
  if (missing.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[LWD] Listing card received non-canonical listing object at "${surface}". ` +
      `Missing: ${missing.join(", ")}`,
      item
    );
  }
}

// ── Deduplicate by name (DB first, static second) ────────────────────────────
// Used when merging DB listings with static data fallback.
export function mergeListings(dbRows, staticRows) {
  const dbNames = new Set(dbRows.map((v) => v.name?.toLowerCase()).filter(Boolean));
  const uniqueStatic = staticRows.filter((v) => !dbNames.has((v.name || "").toLowerCase()));
  return [...dbRows, ...uniqueStatic];
}
